<template>
  <clubVoting
    v-if="showComp"
    :clubID="detailObj.id"
    :clubDetails="detailObj"
    @back="closeComp"
  />

  <!-- ─── CLUB SUMMARY REPORT (REDESIGNED) ─────────────────────────── -->
  <div v-else-if="showClubSummary" class="vr-page">
    <!-- Clean Minimalist Header Bar -->
    <header class="vr-topbar no-print">
      <div class="vr-topbar__inner">
        <div class="vr-topbar__left">
          <button class="vr-btn-back" @click="closeClubSummary" title="Back to clubs">
            <feather-icon icon="ArrowLeftIcon" size="17" />
            <span>Back to Clubs</span>
          </button>
          <div class="vr-topbar__divider"></div>
          <div class="vr-topbar__title-group">
            <div class="vr-topbar__chip">
              <span class="vr-pulse-dot"></span>
              <span>LIVE REPORT</span>
            </div>
            <h1 class="vr-topbar__title">Club Summary Report</h1>
          </div>
        </div>

        <div class="vr-topbar__actions">
          <button
            class="vr-action-btn vr-action-btn--secondary"
            :disabled="!items.length"
            @click="downloadClubCSV"
          >
            <feather-icon icon="DownloadIcon" size="16" />
            <span>Export CSV</span>
          </button>
          <button
            class="vr-action-btn vr-action-btn--primary"
            @click="printReport"
          >
            <feather-icon icon="PrinterIcon" size="16" />
            <span>Print Report</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Report Document / Body -->
    <main class="vr-container">
      <article class="vr-club-card">
        <!-- Card Header -->
        <div class="vr-club-card__header">
          <div class="vr-club-card__title-wrap">
            <div class="vr-club-card__badge-icon">
              <feather-icon icon="LayersIcon" size="20" />
            </div>
            <div>
              <h4 class="vr-club-card__name">All Registered Clubs</h4>
              <div class="vr-club-card__sub-line">
                <span>Overview of seats, enrollment & club details</span>
              </div>
            </div>
          </div>

          <div class="vr-club-card__metrics">
            <div class="vr-pill vr-pill--votes">
              <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
              <strong>{{ items.length }}</strong>
              <span class="ml-25">{{ items.length === 1 ? 'Club' : 'Clubs' }}</span>
            </div>
            <div class="vr-pill vr-pill--share">
              <span>{{ totalOccupied }} / {{ totalSeats }} Seats Filled</span>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="vr-table-responsive">
          <table class="vr-table">
            <thead>
              <tr>
                <th class="vr-th--idx">#</th>
                <th style="min-width: 180px;">Club Name</th>
                <th style="min-width: 260px; white-space: nowrap;">Classes</th>
                <th class="text-center" style="min-width: 120px;">Total Seats</th>
                <th class="text-center" style="min-width: 120px;">Occupied</th>
                <th class="text-center" style="min-width: 120px;">Available</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(club, idx) in items" :key="club.id" class="vr-row">
                <td class="vr-td--idx">
                  <span class="vr-idx-circle">{{ idx + 1 }}</span>
                </td>
                <td>
                  <span class="vr-td--name">{{ club.title }}</span>
                </td>
                <td style="white-space: nowrap;">
                  <span class="vr-tag vr-tag--class text-nowrap">{{ getClass(club.classes) }}</span>
                </td>
                <td class="text-center">
                  <span class="vr-id-badge">{{ club.seats }}</span>
                </td>
                <td class="text-center">
                  <span class="vr-tag vr-tag--section">{{ club.occupied }}</span>
                </td>
                <td class="text-center">
                  <span class="vr-pill vr-pill--votes" style="font-size: 0.88rem; padding: 4px 14px;">{{ club.seats - club.occupied }}</span>
                </td>
              </tr>
              <tr v-if="!items.length">
                <td colspan="6" class="vr-empty-cell">
                  <feather-icon icon="InboxIcon" size="28" class="text-muted mb-1 d-block mx-auto" />
                  <span>No clubs found.</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="vr-row vr-totals-row">
                <td colspan="3" class="text-right font-weight-bold" style="font-size: 1rem; color: #334155; padding: 16px 24px;">
                  Grand Total
                </td>
                <td class="text-center font-weight-bold" style="font-size: 1.05rem; color: #0f172a;">
                  {{ totalSeats }}
                </td>
                <td class="text-center font-weight-bold" style="font-size: 1.05rem; color: #6d28d9;">
                  {{ totalOccupied }}
                </td>
                <td class="text-center font-weight-bold" style="font-size: 1.05rem; color: #065f46;">
                  {{ totalSeats - totalOccupied }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </article>
    </main>
  </div>

  <!-- ─── VOTING SUMMARY REPORT (REDESIGNED) ────────────────────────────── -->
  <div v-else-if="showVotingSummary" class="vr-page">
    <!-- Clean Minimalist Header Bar -->
    <header class="vr-topbar no-print">
      <div class="vr-topbar__inner">
        <div class="vr-topbar__left">
          <button class="vr-btn-back" @click="closeVotingSummary" title="Back to clubs">
            <feather-icon icon="ArrowLeftIcon" size="17" />
            <span>Back to Clubs</span>
          </button>
          <div class="vr-topbar__divider"></div>
          <div class="vr-topbar__title-group">
            <div class="vr-topbar__chip">
              <span class="vr-pulse-dot"></span>
              <span>LIVE REPORT</span>
            </div>
            <h1 class="vr-topbar__title">Voting Summary Report</h1>
          </div>
        </div>

        <div class="vr-topbar__actions">
          <button
            class="vr-action-btn vr-action-btn--secondary"
            :disabled="votingLoading"
            @click="downloadVotingCSV"
          >
            <feather-icon icon="DownloadIcon" size="16" />
            <span>Export CSV</span>
          </button>
          <button
            class="vr-action-btn vr-action-btn--primary"
            @click="printReport"
          >
            <feather-icon icon="PrinterIcon" size="16" />
            <span>Print Report</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="votingLoading" class="vr-state vr-state--loading">
      <div class="vr-spinner-box">
        <b-spinner variant="primary" style="width: 3.2rem; height: 3.2rem;" />
      </div>
      <h5 class="mt-2 font-weight-bolder text-dark">Aggregating Voting Data...</h5>
      <p class="text-muted small">Fetching latest student club ballots from database</p>
    </div>

    <!-- Error State -->
    <div v-else-if="votingError" class="vr-state vr-state--error">
      <div class="vr-error-icon">
        <feather-icon icon="AlertCircleIcon" size="44" class="text-danger" />
      </div>
      <h5 class="mt-2 font-weight-bolder text-danger">Failed to Load Report</h5>
      <p class="text-muted small mb-2">{{ votingError }}</p>
      <b-button variant="primary" size="sm" @click="loadVotingSummary">
        Retry Now
      </b-button>
    </div>

    <!-- Report Document / Body -->
    <main v-else class="vr-container">
      <!-- Per-Club Cards with Tables -->
      <template v-if="votingGrouped.length > 0">
        <article
          v-for="(group, gIdx) in votingGrouped"
          :key="gIdx"
          class="vr-club-card"
        >
          <!-- Club Card Header -->
          <div class="vr-club-card__header">
            <div class="vr-club-card__title-wrap">
              <div class="vr-club-card__badge-icon">
                <feather-icon icon="UsersIcon" size="20" />
              </div>
              <div>
                <h4 class="vr-club-card__name">{{ group.clubName }}</h4>
                <div class="vr-club-card__sub-line">
                  <span>Roster of verified voters</span>
                </div>
              </div>
            </div>

            <div class="vr-club-card__metrics">
              <div class="vr-pill vr-pill--votes">
                <feather-icon icon="ThumbsUpIcon" size="14" class="mr-25" />
                <strong>{{ group.votes.length }}</strong>
                <span class="ml-25">{{ group.votes.length === 1 ? 'Vote' : 'Votes' }}</span>
              </div>
              <div class="vr-pill vr-pill--share">
                <span>{{ totalVotes ? Math.round((group.votes.length / totalVotes) * 100) : 0 }}% Share</span>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="vr-table-responsive">
            <table class="vr-table">
              <thead>
                <tr>
                  <th class="vr-th--idx">#</th>
                  <th class="vr-th--id">Student ID</th>
                  <th class="vr-th--name">Student Name</th>
                  <th class="vr-th--class">Class</th>
                  <th class="vr-th--section">Section</th>
                  <th class="vr-th--club">Club</th>
                  <th class="vr-th--date">Voted On</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(vote, vIdx) in group.votes" :key="vIdx" class="vr-row">
                  <td class="vr-td--idx">
                    <span class="vr-idx-circle">{{ vIdx + 1 }}</span>
                  </td>
                  <td class="vr-td--id">
                    <span class="vr-id-badge">{{ vote.studentID || '—' }}</span>
                  </td>
                  <td class="vr-td--name">
                    {{ vote.studentName || vote.name || '—' }}
                  </td>
                  <td class="vr-td--class">
                    <span class="vr-tag vr-tag--class">{{ vote.className || vote.class || '—' }}</span>
                  </td>
                  <td class="vr-td--section">
                    <span class="vr-tag vr-tag--section">{{ vote.sectionName ? 'Sec ' + vote.sectionName : '—' }}</span>
                  </td>
                  <td class="vr-td--club">
                    <span class="vr-club-name-tag">{{ vote.clubName || group.clubName || '—' }}</span>
                  </td>
                  <td class="vr-td--date">
                    <div class="vr-date-cell">
                      <feather-icon icon="ClockIcon" size="14" class="text-muted mr-50" />
                      <span>{{ formatVoteDate(vote.votedDate) }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!group.votes.length">
                  <td colspan="7" class="vr-empty-cell">
                    <feather-icon icon="InboxIcon" size="28" class="text-muted mb-1 d-block mx-auto" />
                    <span>No ballots recorded for this club yet.</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </template>

      <!-- Flat fallback when data is not grouped -->
      <div v-else-if="votingSummaryData.length > 0" class="vr-club-card">
        <div class="vr-club-card__header">
          <h4 class="vr-club-card__name">All Recorded Votes</h4>
          <span class="vr-pill vr-pill--votes">{{ votingSummaryData.length }} Total</span>
        </div>
        <div class="vr-table-responsive">
          <table class="vr-table">
            <thead>
              <tr>
                <th class="vr-th--idx">#</th>
                <th v-for="col in votingFlatCols" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rIdx) in votingSummaryData" :key="rIdx" class="vr-row">
                <td class="vr-td--idx"><span class="vr-idx-circle">{{ rIdx + 1 }}</span></td>
                <td v-for="col in votingFlatCols" :key="col">{{ row[col] || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="vr-empty-state">
        <div class="vr-empty-icon">
          <feather-icon icon="InboxIcon" size="48" />
        </div>
        <h4 class="font-weight-bold text-dark mt-2">No Voting Data Available</h4>
        <p class="text-muted small">No students have cast votes in any club yet.</p>
      </div>

    </main>
  </div>

  <!-- ─── MAIN CLUBS LIST ──────────────────────────────────────────────── -->
  <div v-else>
    <b-sidebar
      bg-variant="white"
      v-model="visibility2"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Settings</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility2 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row v-if="saveObj">
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <h5 class="mr-1">{{ $t("Active Joining link") }}</h5>
            <b-form-checkbox v-model="saveObj.valueBool" switch />
          </b-col>

          <b-col md="12" class="mt-2">
            <b-button
              variant="success"
              :disabled="savingSetting"
              @click="saveSetting()"
              block
            >
              <b-spinner v-if="savingSetting" small type="grow" />
              <span v-else class="align-middle">Save</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      <sidebar-content />
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Manage Club</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="">
          <b-col md="12">
            <b-form-group
              label="Cover"
              invalid-feedback="Cover is required"
              ref="cover"
            >
              <div>
                <b-spinner
                  v-if="coverLoading"
                  type="grow"
                  label="Loading..."
                  variant="primary"
                  class="m-5"
                />
                <b-img
                  v-else-if="!myObj.cover"
                  ref="previewEl"
                  src="https://cdn.cloudious.net/file-1678189226453-406060527.png"
                  @click="$refs.coverImg.click()"
                  style="border-radius: 10px"
                  height="120px"
                  width="120px"
                />
                <div v-else class="d-flex align-items-center">
                  <b-img
                    ref="previewEl"
                    :src="myObj.cover"
                    style="border-radius: 10px"
                    height="120px"
                    width="120px"
                  />

                  <feather-icon
                    icon="Trash2Icon"
                    size="22"
                    class="text-danger cursor-pointer ml-1"
                    @click="removeCover()"
                  />
                </div>
                <input
                  type="file"
                  id="coverImg"
                  hidden
                  ref="coverImg"
                  @change="addCover()"
                  accept="image/*"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Title"
              invalid-feedback="Title is required"
              ref="title"
            >
              <b-form-input
                v-model.trim="myObj.title"
                placeholder="Enter title"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Total Seats"
              invalid-feedback="Total Seats is required."
              ref="seats"
            >
              <b-form-input
                type="number"
                v-model="myObj.seats"
                placeholder="Enter total seats"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Classes"
              :invalid-feedback="$t('Class is required')"
              ref="class"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="classIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                :disabled="dataLoading"
                placeholder="Select classes"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group label="Description" ref="desc">
              <b-form-textarea
                v-model.trim="myObj.description"
                rows="3"
                placeholder="Enter club description"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-button
              variant="success"
              :disabled="request || coverLoading"
              @click="save()"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else class="align-middle">Save</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      <sidebar-content />
    </b-sidebar>

    <b-card>
      <b-row class="mt-1">
        <b-col xl="2" lg="3" md="4" sm="12">
          <b-button
            variant="primary"
            block
            @click="AddOpen(0)"
            :disabled="dataLoading"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="align-middle">Club</span>
          </b-button>
        </b-col>
        <b-col
          xl="10"
          lg="9"
          md="8"
          sm="12"
          class="mt-md-0 mt-1 d-flex align-items-center"
        >
          <b-form-group class="w-100 mb-0">
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>

              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('Search') + '...'"
              >
              </b-form-input>
            </b-input-group>
          </b-form-group>

          <!-- ── REPORT DROPDOWN ──────────────────────────────── -->
          <b-dropdown
            variant="outline-primary"
            class="report-dropdown ml-50 flex-shrink-0"
            right
          >
            <template #button-content>
              <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
              <span>Report</span>
            </template>
            <b-dropdown-item @click="openClubSummary">
              <feather-icon icon="BarChart2Icon" class="mr-50" />
              Club Summary
            </b-dropdown-item>
            <b-dropdown-item @click="openVotingSummary">
              <feather-icon icon="ThumbsUpIcon" class="mr-50" />
              Voting Summary
            </b-dropdown-item>
          </b-dropdown>
          <!-- ───────────────────────────────────────────────────── -->

          <b-button
            variant="outline-primary"
            class="settings-btn ml-50 flex-shrink-0"
            v-b-tooltip.hover.top
            :title="$t('Settings')"
            @click="openSettings()"
          >
            <feather-icon icon="SettingsIcon" size="16" />
          </b-button>
        </b-col>
      </b-row>
      <b-table
        class="mt-1"
        :busy="itemLoading"
        :items="filters"
        :fields="fields"
        show-empty
        responsive
        hover
        small
      >
        <template #empty="scope">
          <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
        </template>
        <template #table-busy>
          <div
            class="d-flex justify-content-center mb-3"
            style="margin-top: 50px"
          >
            <b-spinner
              style="width: 3rem; height: 3rem"
              type="grow"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </div>
        </template>

        <template #cell(cover)="data">
          <b-img
            v-if="data.item.cover"
            rounded
            :src="data.item.cover"
            height="100px"
            width="100px"
            alt=""
          />
          <b-img
            v-else
            rounded
            src="https://cdn.cloudious.net/file-1678189226453-406060527.png"
            height="100px"
            width="100px"
            alt=""
          />
        </template>
        <template #cell(title)="data">
          <b-badge variant="light-primary">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(classes)="data">
          <b-badge variant="light-primary">
            {{ getClass(data.item.classes) }}
          </b-badge>
        </template>
        <template #cell(seats)="data">
          <b-badge variant="light-primary">
            {{ data.item.occupied }} / {{ data.value }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex">
            <b-button
              variant="primary"
              v-b-tooltip.hover.bottomleft
              title="View Votes"
              class="btn-icon mr-1"
              @click="openComp(data.item)"
            >
              <feather-icon icon="EyeIcon" class="" />
            </b-button>

            <!-- v-if="rights.edit" -->
            <b-button
              variant="primary"
              v-b-tooltip.hover.bottomleft
              title="Edit"
              class="btn-icon mr-1"
              @click="AddOpen(data.item)"
            >
              <feather-icon icon="EditIcon" class="" />
            </b-button>

            <!-- v-if="rights.delete" -->
            <b-button
              variant="outline-danger"
              v-b-tooltip.hover.bottomright
              title="Delete"
              class="btn-icon"
              @click="Delete(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BContainer,
  BMediaAside,
  BMediaBody,
  BSpinner,
  BCardTitle,
  BSidebar,
  BCardText,
  BInputGroupPrepend,
  BFormInput,
  BInputGroup,
  BFormTextarea,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";
import clubVoting from "./clubVoting.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BImg,
    BFormCheckbox,
    BFormGroup,
    BForm,
    BContainer,
    BMediaAside,
    BMediaBody,
    BSidebar,
    BSpinner,
    BCardTitle,
    BCardText,
    BInputGroupPrepend,
    BFormInput,
    BInputGroup,
    BFormTextarea,
    vSelect,
    flatPickr,
    clubVoting,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  async created() {
    this.LoadClasses();
    this.LoadData();
    this.LoadSettings();
  },
  computed: {
    filters: function () {
      return this.items.filter((pro) => {
        return (
          pro.title.toLowerCase().match(this.searchQuery.toLowerCase()) ||
          pro.description.toLowerCase().match(this.searchQuery.toLowerCase())
        );
      });
    },

    // ── Club Summary computed ──────────────────────────────────────────
    totalSeats() {
      return this.items.reduce((sum, c) => sum + (parseInt(c.seats) || 0), 0);
    },
    totalOccupied() {
      return this.items.reduce((sum, c) => sum + (parseInt(c.occupied) || 0), 0);
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    // ── Voting Summary computed ────────────────────────────────────────
    votingGrouped() {
      if (!Array.isArray(this.votingSummaryData) || !this.votingSummaryData.length)
        return [];

      const first = this.votingSummaryData[0];

      // If already grouped (array of objects with clubName + votes array)
      if (first && Array.isArray(first.votes)) {
        return this.votingSummaryData;
      }

      // If flat array — group by clubID / clubName / club
      const map = {};
      this.votingSummaryData.forEach((row) => {
        const key = row.clubName || row.club || row.clubTitle || `Club ${row.clubID || row.cID || ""}`;
        if (!map[key]) map[key] = { clubName: key, votes: [] };
        map[key].votes.push(row);
      });
      return Object.values(map);
    },
    totalVotes() {
      return this.votingGrouped.reduce((s, g) => s + (g.votes ? g.votes.length : 0), 0);
    },
    highestVotes() {
      if (!this.votingGrouped.length) return 0;
      return Math.max(...this.votingGrouped.map((g) => g.votes ? g.votes.length : 0));
    },
    avgVotes() {
      if (!this.votingGrouped.length) return 0;
      return (this.totalVotes / this.votingGrouped.length).toFixed(1);
    },
    votingFlatCols() {
      if (!this.votingSummaryData.length) return [];
      return Object.keys(this.votingSummaryData[0]);
    },
  },
  data() {
    return {
      color: ["primary", "success", "warning", "info", "danger", "secondary"],
      rights: {},
      config: {
        enableTime: true,
        altFormat: "j M, Y h:i K",
        altInput: true,
        dateFormat: "Y-m-d\\TH:i:S",
      },
      searchQuery: "",
      dataLoading: false,
      itemLoading: false,
      items: [],
      fields: [
        { label: "cover", key: "cover" },
        { label: "title", key: "title" },
        { label: "classes", key: "classes" },
        { label: "seats", key: "seats" },
        // { label: "description", key: "description" },
        "actions",
      ],
      visibility: false,
      myObj: {
        id: 0,
        cover: "",
        title: "",
        classes: "",
        seats: 0,
        occupied: 0,
        description: "",
        status: "active",
        campusID: this.$store.state.userData.cId,
      },
      currentItem: {},
      showLink: false,
      linkLoading: false,
      request: false,
      classes: [],
      classesOptions: [],
      classIDs: [],
      coverLoading: false,
      showComp: false,
      detailObj: {},
      settingObj: null,
      visibility2: false,
      saveObj: {},
      savingSetting: false,

      // ── Report state ─────────────────────────────────────────────────
      showClubSummary: false,
      showVotingSummary: false,
      votingSummaryData: [],
      votingLoading: false,
      votingError: null,
    };
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    openComp(item) {
      this.showComp = true;
      this.detailObj = item;
      this.detailObj.classNames = this.getClass(this.detailObj.classes);
    },
    closeComp() {
      this.showComp = false;
      this.detailObj = {};
    },
    getClass(cID) {
      let ids = cID
        .split(",")
        .filter((el) => el)
        .map((el) => parseInt(el));
      let names = [];
      ids.forEach((el) => {
        let obj = this.classesOptions.find((elem) => elem.id == el);
        if (obj) names.push(obj.name);
      });
      return names.join(", ");
    },

    async addCover() {
      let media = this.$refs.coverImg.files[0];
      if (media) {
        this.coverLoading = true;
        try {
          const uploaded = await uploadToMySkool([media]);
          this.myObj.cover = getUploadPath(uploaded[0]);
        } catch (err) {
          console.log(err);
        } finally {
          this.coverLoading = false;
        }
      }
    },
    removeCover() {
      this.myObj.cover = "";
      this.$refs.coverImg.files = null;
    },

    AddOpen(id) {
      if (id == 0) {
        this.myObj = {
          id: 0,
          cover: "",
          title: "",
          classes: "",
          seats: 0,
          occupied: 0,
          description: "",
          status: "active",
          campusID: this.$store.state.userData.cId,
        };
        this.classIDs = [];
      } else {
        this.myObj = { ...id };
        this.classIDs = this.myObj.classes
          .split(",")
          .filter((el) => el)
          .map((el) => parseInt(el));
      }
      this.visibility = true;

      var elem = this.$refs["title"];
      elem.state = undefined;
      var elem = this.$refs["seats"];
      elem.state = undefined;
      var elem = this.$refs["class"];
      elem.state = undefined;
    },

    CheckTitle() {
      var elem = this.$refs["title"];
      if (this.myObj.title == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSeats() {
      var elem = this.$refs["seats"];
      let x = parseInt(this.myObj.seats);
      if (isNaN(x) || x <= 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckClass() {
      var elem = this.$refs["class"];
      if (this.classIDs.length == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckDesc() {
      var elem = this.$refs["desc"];
      if (this.myObj.description == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async save() {
      this.CheckTitle();
      this.CheckSeats();
      this.CheckClass();
      // this.CheckDesc();
      if (
        this.CheckTitle() == false ||
        this.CheckSeats() == false ||
        this.CheckClass() == false
        // ||  this.CheckDesc() == false
      ) {
        this.$bvToast.toast(this.$t("Please fill the form correctly."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else {
        this.myObj.seats = parseInt(this.myObj.seats);
        this.myObj.classes = this.classIDs.join(",");
        // console.log(this.myObj);

        if (this.myObj.id == 0) {
          this.request = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Clubs?db=" +
              this.$store.state.userData.db,
            body: this.myObj,
            message: "Club added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;

          if (status) {
            this.LoadData();
            this.visibility = false;
          }
        } else {
          this.request = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "Clubs/" +
              this.myObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.myObj,
            message: "Club updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.LoadData();
            this.visibility = false;
          }
        }
      }
    },
    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Clubs/" +
            id +
            "?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          message: "Club deleted successfully.",
          context: this,
          body: null,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData();
      }
    },

    async LoadData() {
      this.itemLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Clubs?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.items = await this.get(obj);
      this.itemLoading = false;
    },
    async LoadClasses() {
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      this.classes = await this.get(obj);
      this.classesOptions = this.classes.map((el) => el.cls);

      this.dataLoading = false;
    },
    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        body: ["active_club"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      let obj = res.find((el) => el.key == "active_club");
      this.settingObj = obj
        ? obj
        : {
            id: 0,
            campusID: this.$store.state.userData.cId,
            key: "active_club",
            value: "active_club",
            valueBool: false,
            valueNumber: 0,
          };
    },
    openSettings() {
      this.saveObj = { ...this.settingObj };
      this.visibility2 = true;
    },
    async saveSetting() {
      this.savingSetting = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "Settings/SaveNew?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [this.saveObj],
        message: "Setting saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingSetting = false;
      if (status) {
        this.visibility2 = false;
        this.LoadSettings();
      }
    },

    // ── Report methods ───────────────────────────────────────────────────

    openClubSummary() {
      this.showClubSummary = true;
    },
    closeClubSummary() {
      this.showClubSummary = false;
    },

    async openVotingSummary() {
      this.showVotingSummary = true;
      await this.loadVotingSummary();
    },
    closeVotingSummary() {
      this.showVotingSummary = false;
      this.votingSummaryData = [];
      this.votingError = null;
    },
    async loadVotingSummary() {
      this.votingLoading = true;
      this.votingError = null;
      try {
        const data = await this.get({
          url:
            this.$store.state.domain +
            "Clubs/VotingSummary?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.votingSummaryData = Array.isArray(data) ? data : (data ? [data] : []);
      } catch (err) {
        console.error(err);
        this.votingError = "Failed to load voting summary. Please try again.";
      } finally {
        this.votingLoading = false;
      }
    },

    formatVoteDate(dateStr) {
      if (!dateStr) return "—";
      try {
        return new Date(dateStr).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },

    printReport() {
      window.print();
    },

    downloadClubCSV() {
      if (!this.items || !this.items.length) return;

      const headers = ["#", "Club Name", "Classes", "Total Seats", "Occupied", "Available"];
      const rows = [];

      this.items.forEach((club, idx) => {
        const classes = club.classes ? this.getClass(club.classes) : "";
        const seats = Number(club.seats) || 0;
        const occupied = Number(club.occupied) || 0;
        const available = seats - occupied;

        rows.push([
          idx + 1,
          club.title || "",
          classes,
          seats,
          occupied,
          available,
        ]);
      });

      // Grand Total Row
      const totalSeats = Number(this.totalSeats) || 0;
      const totalOccupied = Number(this.totalOccupied) || 0;
      rows.push([
        "",
        "Grand Total",
        "",
        totalSeats,
        totalOccupied,
        totalSeats - totalOccupied,
      ]);

      const escapeCSV = (val) => {
        const str = String(val === undefined || val === null ? "" : val).replace(/"/g, '""');
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str}"`
          : str;
      };

      const csvContent = [
        headers.map(escapeCSV).join(","),
        ...rows.map((r) => r.map(escapeCSV).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `club-summary-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    downloadVotingCSV() {
      if (!this.votingGrouped.length && !this.votingSummaryData.length) return;

      let rows = [];
      let headers = [];

      if (this.votingGrouped.length) {
        // Grouped data → flatten
        headers = ["Student ID", "Student Name", "Class", "Section", "Club", "Voted On"];
        this.votingGrouped.forEach((group) => {
          (group.votes || []).forEach((vote) => {
            rows.push([
              vote.studentID || "",
              vote.studentName || vote.name || "",
              vote.className || vote.class || "",
              vote.sectionName || "",
              vote.clubName || group.clubName || "",
              this.formatVoteDate(vote.votedDate),
            ]);
          });
        });
      } else {
        // Flat data
        headers = this.votingFlatCols;
        this.votingSummaryData.forEach((row) => {
          rows.push(headers.map((h) => (row[h] !== undefined ? row[h] : "")));
        });
      }

      const escapeCSV = (val) => {
        const str = String(val).replace(/"/g, '""');
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str}"`
          : str;
      };

      const csvContent = [
        headers.map(escapeCSV).join(","),
        ...rows.map((r) => r.map(escapeCSV).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `voting-summary-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

/* ─── TOOLBAR ACTION BUTTONS ALIGNMENT ──────────────────────────────── */
::v-deep .report-dropdown {
  display: inline-flex;
  align-items: center;

  .dropdown-toggle {
    height: 38px !important;
    padding: 0 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    font-size: 0.95rem !important;
    font-weight: 500 !important;

    &::after {
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
      top: 0 !important;
      margin-left: 8px !important;
      vertical-align: middle !important;
      display: inline-block !important;
    }
  }
}

.settings-btn {
  height: 38px !important;
  width: 38px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* ─── REPORT PAGE LAYOUT ─────────────────────────────────────────────── */
.clubs-report-page {
  min-height: 100vh;
  background: #f8f9fb;
  font-family: "Inter", "Segoe UI", sans-serif;
}

/* Control bar */
.clubs-report-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #5a3fd8 0%, #7c5ce6 100%);
  padding: 14px 28px;
  box-shadow: 0 2px 16px rgba(90, 63, 216, 0.25);
  position: sticky;
  top: 0;
  z-index: 999;

  &__left,
  &__right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.ctrl-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &--back {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    &:hover { background: rgba(255, 255, 255, 0.28); }
  }
  &--print {
    background: #fff;
    color: #5a3fd8;
    &:hover { background: #ece9fd; }
  }
  &--csv {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    border: 1.5px solid rgba(255, 255, 255, 0.4);
    &:hover { background: rgba(255, 255, 255, 0.28); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
}

/* Loading / error state */
.clubs-report-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

/* Content wrapper */
.clubs-report-content {
  width: 100%;
  max-width: 100%;
  margin: 28px 0 0;
  padding: 0 36px 60px;
}

/* ─── HEADER ─────────────────────────────────────────────────────────── */
.report-header {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  overflow: hidden;
  margin-bottom: 28px;
  display: flex;

  &__accent {
    width: 8px;
    background: linear-gradient(180deg, #5a3fd8 0%, #9b6cff 100%);
    flex-shrink: 0;
    border-radius: 4px 0 0 4px;
  }

  &__body {
    padding: 24px 28px;
    flex: 1;
  }

  &__titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.report-school-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e1e3a;
  margin: 0;
  letter-spacing: -0.5px;
}
.report-subtitle {
  font-size: 1rem;
  font-weight: 600;
  color: #5a3fd8;
  margin: 0;
}
.report-meta {
  font-size: 0.8rem;
  color: #888;
  margin: 0;
}

/* ─── STAT CARDS ─────────────────────────────────────────────────────── */
.report-stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
}

.report-stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  transition: transform 0.2s;
  &:hover { transform: translateY(-2px); }

  &__icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    opacity: 0.75;
  }
  &__value {
    font-size: 1.75rem;
    font-weight: 800;
    margin: 0;
    line-height: 1;
  }

  &--blue   { background: linear-gradient(135deg, #e8f1ff 0%, #dce9ff 100%); color: #2563eb;
              .report-stat-card__icon { background: #2563eb; color: #fff; } }
  &--green  { background: linear-gradient(135deg, #e6fbf2 0%, #d6f5e8 100%); color: #16a34a;
              .report-stat-card__icon { background: #16a34a; color: #fff; } }
  &--purple { background: linear-gradient(135deg, #ede9fe 0%, #e4d8fc 100%); color: #7c3aed;
              .report-stat-card__icon { background: #7c3aed; color: #fff; } }
  &--orange { background: linear-gradient(135deg, #fff5e6 0%, #fdebd0 100%); color: #ea580c;
              .report-stat-card__icon { background: #ea580c; color: #fff; } }
}

/* ─── TABLE SECTIONS ─────────────────────────────────────────────────── */
.report-section {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 24px;
  margin-bottom: 20px;

  &__title {
    font-size: 1rem;
    font-weight: 700;
    color: #1e1e3a;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 2px solid #f0eeff;
  }

  &--club {
    padding-top: 0;
    overflow: hidden;
  }
}

.report-club-header {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #5a3fd8 0%, #7c5ce6 100%);
  color: #fff;
  padding: 14px 20px;
  margin-bottom: 0;
  border-radius: 14px 14px 0 0;

  h5 {
    color: #fff;
    font-weight: 700;
    font-size: 0.95rem;
    flex: 1;
  }
}

.report-club-badge {
  background: rgba(255,255,255,0.22);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.35);
}

/* ─── TABLE ──────────────────────────────────────────────────────────── */
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  thead tr {
    background: #f5f3ff;
  }

  th {
    padding: 10px 14px;
    font-weight: 700;
    color: #5a3fd8;
    text-align: left;
    border-bottom: 2px solid #e8e3fc;
    white-space: nowrap;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  td {
    padding: 10px 14px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #faf9ff;
  }

  tfoot .totals-row {
    background: linear-gradient(135deg, #f0eeff 0%, #e8e3fc 100%);
    td {
      font-weight: 700;
      color: #5a3fd8;
      border-top: 2px solid #c4b5fd;
      padding-top: 12px;
      padding-bottom: 12px;
    }
  }
}

/* ─── FOOTER ─────────────────────────────────────────────────────────── */
.report-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  font-size: 0.78rem;
  color: #9ca3af;
}

/* ─── CARD HOVER (main list) ─────────────────────────────────────────── */
.card {
  transition: 0.4s;
}
.card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
}

/* ─── REDESIGNED VOTING SUMMARY STYLES (vr-*) ──────────────────────── */
.vr-page {
  min-height: 100vh;
  background: #f8fafc;
  color: #1e293b;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding-bottom: 60px;
}

/* Full Width Modern Topbar */
.vr-topbar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 999;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  &__inner {
    width: 100%;
    padding: 16px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__divider {
    width: 1px;
    height: 28px;
    background: #e2e8f0;
    margin: 0 18px;
  }

  &__title-group {
    display: flex;
    flex-direction: column;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #4f46e5;
    letter-spacing: 0.8px;
    margin-bottom: 2px;
  }

  &__title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.3px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.vr-pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 2.5px rgba(16, 185, 129, 0.25);
}

.vr-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 9px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
}

.vr-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 9px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease-in-out;

  &--secondary {
    background: #ffffff;
    color: #334155;
    border: 1px solid #cbd5e1;
    &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
      color: #0f172a;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &--primary {
    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.25);
    &:hover {
      background: linear-gradient(135deg, #4338ca 0%, #4f46e5 100%);
      box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
      transform: translateY(-1px);
    }
  }
}

/* Loading & Error States */
.vr-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 90px 20px;
  text-align: center;

  &--loading .vr-spinner-box {
    background: #eef2ff;
    padding: 18px;
    border-radius: 22px;
  }

  &--error .vr-error-icon {
    background: #fee2e2;
    padding: 16px;
    border-radius: 22px;
  }
}

/* Full Width Container */
.vr-container {
  width: 100%;
  margin: 28px 0 0;
  padding: 0 36px;
}

/* Club Cards */
.vr-club-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  margin-bottom: 28px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  }

  &__header {
    padding: 20px 28px;
    background: #fbfcfe;
    border-bottom: 1px solid #edf2f7;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__badge-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 8px rgba(79, 70, 229, 0.25);
  }

  &__name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.2px;
  }

  &__sub-line {
    font-size: 0.85rem;
    color: #64748b;
    margin-top: 2px;
  }

  &__metrics {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.vr-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 9999px;

  &--votes {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }
  &--share {
    background: #eef2ff;
    color: #3730a3;
    border: 1px solid #c7d2fe;
  }
}

/* Table */
.vr-table-responsive {
  overflow-x: auto;
  width: 100%;
}

.vr-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.96rem;

  thead tr {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
  }

  th {
    padding: 14px 24px;
    font-size: 0.82rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    white-space: nowrap;
  }

  td {
    padding: 18px 24px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }

  .vr-row:hover {
    background: #f8fafc;
  }

  .vr-row:last-child td {
    border-bottom: none;
  }

  .vr-totals-row {
    background: #f8fafc;
    border-top: 2px solid #e2e8f0;

    td {
      border-top: 2px solid #e2e8f0 !important;
      border-bottom: none !important;
      background: #f8fafc;
    }
  }
}

.vr-th--idx,
.vr-td--idx {
  width: 58px;
  text-align: center;
}

.vr-idx-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
}

.vr-id-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  letter-spacing: 0.5px;
}

.vr-td--name {
  font-size: 0.98rem;
  font-weight: 600;
  color: #1e293b;
}

.vr-tag {
  display: inline-block;
  font-size: 0.88rem;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 7px;

  &--class {
    background: #f1f5f9;
    color: #334155;
    border: 1px solid #e2e8f0;
    white-space: nowrap !important;
    font-weight: 500;
  }
  &--section {
    background: #ede9fe;
    color: #6d28d9;
    border: 1px solid #ddd6fe;
    font-weight: 600;
  }
}

.vr-club-name-tag {
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
}

.vr-date-cell {
  display: inline-flex;
  align-items: center;
  font-size: 0.92rem;
  font-weight: 500;
  color: #475569;
}

.vr-empty-cell {
  padding: 40px 20px !important;
  text-align: center;
  color: #94a3b8;
  font-size: 0.95rem;
}

.vr-empty-state {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 70px 20px;
  text-align: center;
  margin-bottom: 24px;

  .vr-empty-icon {
    color: #94a3b8;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<!-- ─── PRINT STYLES ──────────────────────────────────────────────────── -->
<style>
@media print {
  @page {
    margin: 10mm 12mm;
    size: auto;
  }

  .no-print,
  .clubs-report-controls,
  .vr-topbar,
  nav,
  aside,
  header,
  footer,
  .main-menu,
  .navbar,
  [class*="sidebar"] {
    display: none !important;
  }

  body,
  .clubs-report-page,
  .vr-page {
    background: #fff !important;
    padding: 0 !important;
  }

  .clubs-report-content {
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .report-header {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }

  .report-stat-cards {
    grid-template-columns: repeat(4, 1fr) !important;
  }

  .report-stat-card {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }

  .report-section {
    box-shadow: none !important;
    border: 1px solid #ddd;
    page-break-inside: avoid;
  }

  .report-table th,
  .report-table td {
    font-size: 11px !important;
    padding: 6px 10px !important;
  }

  .report-club-header {
    background: #5a3fd8 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ─── VOTING SUMMARY PRINT STYLES (LARGE & PROMINENT) ────────────── */
  .vr-container {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 16px 20px !important;
  }

  .vr-club-card {
    box-shadow: none !important;
    border: 1.5px solid #cbd5e1 !important;
    border-radius: 12px !important;
    page-break-inside: avoid;
    margin-bottom: 24px !important;
  }

  .vr-club-card__header {
    padding: 16px 22px !important;
    border-bottom: 1.5px solid #cbd5e1 !important;
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-club-card__name {
    font-size: 19px !important;
    font-weight: 600 !important;
    color: #0f172a !important;
  }

  .vr-club-card__sub-line {
    font-size: 13px !important;
    color: #64748b !important;
  }

  .vr-club-card__badge-icon {
    width: 40px !important;
    height: 40px !important;
    border-radius: 10px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-pill {
    font-size: 13px !important;
    padding: 6px 15px !important;
    font-weight: 600 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-table {
    font-size: 14px !important;
    width: 100% !important;
  }

  .vr-table th {
    font-size: 13.5px !important;
    padding: 13px 18px !important;
    font-weight: 600 !important;
    color: #334155 !important;
    border-bottom: 2px solid #cbd5e1 !important;
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-table td {
    font-size: 14px !important;
    padding: 14px 18px !important;
    color: #0f172a !important;
    border-bottom: 1px solid #e2e8f0 !important;
    vertical-align: middle !important;
  }

  .vr-th--idx,
  .vr-td--idx {
    width: 48px !important;
    text-align: center !important;
  }

  .vr-idx-circle {
    width: 26px !important;
    height: 26px !important;
    font-size: 13px !important;
    border: 1px solid #cbd5e1 !important;
  }

  .vr-id-badge {
    font-size: 13.5px !important;
    font-weight: 700 !important;
    padding: 4px 10px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-td--name {
    font-size: 14.5px !important;
    font-weight: 600 !important;
    color: #1e293b !important;
  }

  .vr-tag {
    font-size: 13px !important;
    padding: 4px 11px !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    white-space: nowrap !important;
    font-weight: 500 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-club-name-tag {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #0f172a !important;
  }

  .vr-date-cell {
    font-size: 13.5px !important;
    color: #334155 !important;
  }

  .vr-totals-row {
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .vr-totals-row td {
    border-top: 2px solid #cbd5e1 !important;
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
